import React, { useMemo, useState } from 'react';
import {
  StyleSheet,
  View,
  Text,
  TouchableOpacity,
  Platform,
} from 'react-native';
import { useAppDispatch, useAppSelector } from '../../../redux/helper';
import { setSelectedRange } from '../../../redux/backoffice/dashboardSlice';
import { GTFonts, useTheme } from '../../../theme';
import { usePickerItemColor } from '../../../hooks/usePickerItemColor';
import { s, fs, iconSize, v } from '../../../utils/libs';
import { Picker } from '@react-native-picker/picker';
import FontAwesome6 from '@react-native-vector-icons/fontawesome6/static';
import { Menu, useTheme as usePaperTheme } from 'react-native-paper';
import type { DashboardRange } from '../../../utils/dashboardPeriods';

const options: ReadonlyArray<{ value: DashboardRange; label: string }> = [
  { value: 'Today', label: 'Today' },
  { value: 'Yesterday', label: 'Yesterday' },
  { value: 'Weekly', label: 'This Week' },
  { value: 'Monthly', label: 'This Month' },
  { value: 'Yearly', label: 'This Year' },
] as const;

type DateRangeDropdownProps = {
  variant?: 'default' | 'compact';
  chipBgColor?: string;
  chipTextColor?: string;
  chipBorderColor?: string;
  // Optional overrides so other screens/slices can reuse this without coupling to dashboard slice
  selectedOverride?: DashboardRange | undefined;
  onChange?: (value: DashboardRange) => void;
};

const DateRangeDropdown: React.FC<DateRangeDropdownProps> = ({
  variant = 'default',
  chipBgColor,
  chipTextColor,
  chipBorderColor,
  selectedOverride,
  onChange,
}) => {
  const dispatch = useAppDispatch();
  const { theme } = useTheme();
  const { pickerItemColor } = usePickerItemColor();
  const selected = useAppSelector(
    state => state.backofficeDashboard.selectedRange,
  );
  const styles = useMemo(() => createStyles(theme), [theme]);
  const [open, setOpen] = useState(false);
  const paperTheme = usePaperTheme();

  const handleSelect = (value: DashboardRange) => {
    if (onChange) {
      onChange(value);
    } else {
      dispatch(setSelectedRange(value));
    }
  };

  return variant === 'compact' ? (
    <>
      <Menu
        visible={open}
        onDismiss={() => setOpen(false)}
        anchor={
          <TouchableOpacity
            onPress={() => setOpen(true)}
            activeOpacity={0.8}
            style={[
              styles.compactChip,
              chipBgColor
                ? {
                    // backgroundColor: chipBgColor,
                    borderColor: chipBorderColor ?? 'transparent',
                    // borderWidth: chipBorderColor ? 1 : 0,
                  }
                : null,
              !chipBgColor && chipBorderColor
                ? {
                    borderColor: chipBorderColor,
                    borderWidth: 1,
                  }
                : null,
            ]}>
            <Text
              style={[
                styles.compactText,
                chipTextColor ? { color: chipTextColor } : null,
              ]}>
              {options.find(o => o.value === (selectedOverride ?? selected))
                ?.label ??
                selectedOverride ??
                selected}
            </Text>
            <FontAwesome6
              name="chevron-down"
              iconStyle="solid"
              size={iconSize(12)}
              color={chipTextColor || '#fff'}
            />
          </TouchableOpacity>
        }
        contentStyle={{ backgroundColor: paperTheme.colors.surface }}
        anchorPosition="bottom">
        {options.map(o => {
          const isActive = (selectedOverride ?? selected) === o.value;
          return (
            <Menu.Item
              key={o.value}
              title={o.label}
              onPress={() => {
                handleSelect(o.value);
                setOpen(false);
              }}
              leadingIcon={isActive ? 'check' : undefined}
              titleStyle={[
                {
                  color: paperTheme.colors.onSurface,
                  paddingVertical: s(2),
                },
                isActive && { color: theme.colors.success },
              ]}
              dense
            />
          );
        })}
      </Menu>
    </>
  ) : (
    <View style={styles.container}>
      <Picker
        selectedValue={selectedOverride ?? selected}
        onValueChange={itemValue => handleSelect(itemValue as DashboardRange)}
        style={styles.picker}
        dropdownIconColor={theme.colors.text}
        mode="dropdown">
        {options.map(o => (
          <Picker.Item
            key={o.value}
            label={o.label}
            value={o.value}
            color={pickerItemColor}
          />
        ))}
      </Picker>
    </View>
  );
};

export default DateRangeDropdown;

const createStyles = (theme: any) =>
  StyleSheet.create({
    container: {
      backgroundColor: theme.colors.surface,
      borderRadius: theme.borderRadius.md,
      borderWidth: 1,
      borderColor: theme.colors.border,
      paddingHorizontal: s(6),
    },
    picker: {
      height: v(48),
      color: theme.colors.text,
      width: s(180),
      ...(Platform.OS === 'web' && {
        borderWidth: 0,
        backgroundColor: 'transparent',
        outlineStyle: 'none',
        outlineWidth: 0,
        outlineColor: 'transparent',
        paddingHorizontal: 12,
      }),
    } as any,
    compactChip: {
      flexDirection: 'row',
      alignItems: 'center',
      gap: s(6) as unknown as number,
      paddingHorizontal: 0,
      paddingVertical: s(2),
      borderRadius: theme.borderRadius.md,
      borderWidth: 0,
      borderColor: 'transparent',
      backgroundColor: 'transparent',
    },
    compactText: {
      color: '#fff',
      fontFamily: GTFonts.regular,
      fontSize: fs(14),
      lineHeight: fs(20),
      marginLeft: s(2),
    },
    optionRow: {
      paddingVertical: s(12),
      paddingHorizontal: s(12),
      borderBottomWidth: StyleSheet.hairlineWidth,
      borderBottomColor: theme.colors.border,
    },
    optionRowActive: {
      backgroundColor: theme.colors.background,
    },
    optionText: {
      color: theme.colors.text,
      textAlign: 'center',
      fontFamily: GTFonts.regular,
      fontSize: fs(14),
      lineHeight: fs(18),
    },
    optionTextActive: {
      color: theme.colors.success,
      // fontFamily: GTFonts.semibold,
    },
  });
